import { useCallback, useEffect, useRef, useState } from "react";
import { getHistorySummary, requestHistorySummary } from "../../../../service/AiDraftService";

export const HISTORY_POLL_MS = 2000;

const messageOf = (response) => response?.data?.message || null;

/**
 * The AI history summary of the open study: loads it, starts a new one, and polls every 2 s while
 * one is being generated (the backend asks the AI service in the background). The state lives on
 * the server, so closing and reopening the box, or another workstation, shows the same summary.
 *
 * @param studyId DB id of the open study
 * @param active  false when nothing should be fetched (not a radiologist, box closed)
 */
const useHistorySummary = (studyId, active = true) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const studyRef = useRef(studyId);
  studyRef.current = studyId;

  const refresh = useCallback(async () => {
    if (!studyId) return;
    const response = await getHistorySummary(studyId);
    if (studyRef.current !== studyId) return; // the study changed while this was loading
    setData(response?.status === 200 ? response.data?.data || { enabled: false } : { enabled: false });
  }, [studyId]);

  useEffect(() => {
    setData(null);
    setError(null);
    if (active && studyId) refresh();
  }, [studyId, active, refresh]);

  const running = !!data?.summary?.running;
  useEffect(() => {
    if (!active || !running) return undefined;
    const timer = setInterval(refresh, HISTORY_POLL_MS);
    return () => clearInterval(timer);
  }, [active, running, refresh]);

  const generate = useCallback(async () => {
    if (!studyId || busy) return;
    setBusy(true);
    setError(null);
    try {
      const response = await requestHistorySummary(studyId);
      if (response?.status === 202) {
        setData((prev) => ({ ...(prev || { enabled: true }), summary: response.data?.data || null }));
      } else {
        setError(messageOf(response) || "Could not start the history summary");
        await refresh();
      }
    } finally {
      setBusy(false);
    }
  }, [studyId, busy, refresh]);

  return { data, busy, error, generate, refresh };
};

export default useHistorySummary;
